import { useCallback } from "react";

import { DataProxy } from "@apollo/client/cache";
import { useJitsu } from "@jitsu/react";
import { produce } from "immer";

import { ErrorKind, useErrorCallback } from "@/data/error/useError";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import {
  ResourceFragment,
  TaskDocument,
  TaskQuery,
  useCreateResourceMutation,
} from "@/generated/graphql";

/** Callback to add a new resource */
type OnAddResource = (name: string, url: string) => void;
type CallbackTuple = [OnAddResource, boolean];

export function useAddResource(
  assessmentId: string,
  taskId: string
): CallbackTuple {
  const onError = useErrorCallback(ErrorKind.EditTask);
  const { track } = useJitsu();

  const [mutate, { loading }] = useCreateResourceMutation({
    onError,
  });

  const addResource = useCallback<OnAddResource>(
    (name, url) => {
      mutate({
        variables: {
          assessmentId,
          taskId,
          name,
          url,
          mediaId: null,
        },
        update: (proxy, { data }) => {
          if (data && data.createResource) {
            addResourceToCache(
              proxy,
              assessmentId,
              taskId,
              data.createResource
            );
          }
        },
        onCompleted: (data) => {
          track(JitsuEvent.RESOURCE_ADDED, {
            resource_id: data.createResource.id,
            resource_name: data.createResource.name,
            resource_url: data.createResource.url,
          });
        },
      });
    },
    [assessmentId, taskId, mutate, track]
  );

  return [addResource, loading];
}

export function addResourceToCache(
  proxy: DataProxy,
  assessmentId: string,
  taskId: string,
  resource: ResourceFragment
) {
  const cacheData = proxy.readQuery<TaskQuery>({
    query: TaskDocument,
    variables: { assessmentId, taskId },
  });

  if (cacheData) {
    const newData = produce(cacheData, (draft) => {
      if (draft.task) draft.task.resources.push(resource);
    });
    proxy.writeQuery<TaskQuery>({
      query: TaskDocument,
      variables: { assessmentId, taskId },
      data: newData,
    });
  }
}
